import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Star, Quote } from "lucide-react";

const FILTERS = [["all", "Alle"], ["5", "5 sterren"], ["4", "4 sterren"], ["3", "3 sterren"], ["2", "2 sterren"], ["1", "1 ster"]];
const STATUSES = [["new", "Nieuw"], ["reviewed", "Bekeken"], ["published", "Gepubliceerd"], ["archived", "Gearchiveerd"]];
const REC = { ja: "Ja", misschien: "Misschien", nee: "Nee" };

function StarRow({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className={i <= n ? "fill-brand-pink text-brand-pink" : "text-brand-border"} />)}
    </div>
  );
}

export default function Reviews() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = filter === "all" ? {} : { rating: parseInt(filter) };
    api.get("/admin/feedback", { params }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/feedback/${id}`, patch);
      toast.success("Bijgewerkt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stats = data?.stats;

  return (
    <div data-testid="admin-reviews">
      <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Reviews & Feedback</h1>
      <p className="text-brand-muted mt-1">Bekijk klantfeedback en kies wat je als testimonial op de website toont.</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-2xl border border-brand-border p-5">
            <div className="flex items-center gap-2"><span className="font-display text-3xl font-semibold text-brand-charcoal">{stats.average || "–"}</span><Star size={20} className="fill-brand-pink text-brand-pink" /></div>
            <p className="text-sm text-brand-muted mt-1">Gemiddelde score</p>
          </div>
          <div className="bg-white rounded-2xl border border-brand-border p-5">
            <p className="font-display text-3xl font-semibold text-brand-charcoal">{stats.total}</p>
            <p className="text-sm text-brand-muted mt-1">Totaal feedback</p>
          </div>
          <div className="bg-white rounded-2xl border border-brand-border p-5">
            <p className="font-display text-3xl font-semibold text-brand-charcoal">{stats.counts["5"]}</p>
            <p className="text-sm text-brand-muted mt-1">5-sterren reviews</p>
          </div>
          <div className="bg-white rounded-2xl border border-brand-border p-5">
            <p className="font-display text-3xl font-semibold text-brand-charcoal">{stats.counts["4"]}</p>
            <p className="text-sm text-brand-muted mt-1">4-sterren reviews</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-6">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`review-filter-${v}`} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${filter === v ? "bg-brand-charcoal text-white border-brand-charcoal" : "bg-white border-brand-border text-brand-charcoal"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-pink" size={28} /></div>
      ) : !data?.items.length ? (
        <div className="text-center bg-white rounded-3xl border border-brand-border p-12 mt-6">
          <Quote className="mx-auto text-brand-pink mb-3" size={30} />
          <p className="text-brand-charcoal/80">Nog geen feedback ontvangen.</p>
        </div>
      ) : (
        <div className="space-y-3 mt-6">
          {data.items.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border border-brand-border p-5" data-testid={`review-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <StarRow n={f.overall_rating} />
                    <span className="font-medium text-brand-charcoal">{f.first_name} {f.last_name?.[0]}.</span>
                  </div>
                  <p className="text-sm text-brand-muted mt-1">{f.service_name} · {f.date_display}</p>
                </div>
                {f.recommendation && <span className="text-xs bg-brand-blush text-brand-charcoal px-3 py-1 rounded-full">Aanraden: {REC[f.recommendation] || f.recommendation}</span>}
              </div>
              {f.text && <p className="text-brand-charcoal/85 mt-3 text-[15px] leading-relaxed">“{f.text}”</p>}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-brand-muted">
                {f.result_rating ? <span className="flex items-center gap-1">Resultaat: <StarRow n={f.result_rating} /></span> : null}
                {f.service_rating ? <span className="flex items-center gap-1">Service: <StarRow n={f.service_rating} /></span> : null}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-brand-border">
                <select value={f.status} onChange={(e) => update(f.id, { status: e.target.value })} data-testid={`review-status-${f.id}`} className="rounded-xl border border-brand-border px-3 py-2 text-sm focus:border-brand-pink focus:outline-none">
                  {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-brand-charcoal">
                  <input type="checkbox" checked={!!f.show_as_testimonial} onChange={(e) => update(f.id, { show_as_testimonial: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid={`review-testimonial-${f.id}`} />
                  Toon als testimonial op website
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
